import React, { useCallback, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'
import { State } from '../types/state'
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { SelectedSubject } from '../types/selected-subject'
import { useIsLargeView, useIsMobileView } from '../hooks/useIsMobileView'
import { SelectionStatus } from '../types/app-state'
import ConfirmSelectionDialog from './ConfirmSelectionDialog'
import { IndexRequirementType } from '../types/subject'

const SubjectsSelection = (): JSX.Element => {
  const { subjects, app, subjectGroups, selectedSubjects } = useSelector(
    (state: State) => state.user
  )
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const [errors, setErrors] = useState<string[]>([])
  const isMobile = useIsMobileView()
  const isLargeView = useIsLargeView()
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)

  const subjectsToSelectNumber =
    (app?.highLevelSubjectNumber ?? 0) + (app?.standardLevelSubjectNumber ?? 0)

  const initSubjectsSelection = {
    selectedSubjects:
      selectedSubjects.length > 0
        ? selectedSubjects
        : Array.from({ length: subjectsToSelectNumber }).map(_ => ({
          isHighLevel: false
        }))
  } as { selectedSubjects: SelectedSubject[] }

  useEffect(() => {
    validate(formik.values)
  }, [])

  const validate = (subjectsSelection: {
    selectedSubjects: SelectedSubject[]
  }) => {
    const errors: Partial<{
      selectedSubjects: SelectedSubject[]
    }> = {}

    setErrors([])
    if (subjectsSelection.selectedSubjects.some(s => !s?.subjectId)) {
      return
    }

    if (
      subjectsSelection.selectedSubjects.some(
        s =>
          s === null ||
          s === undefined ||
          s?.subjectId === undefined ||
          s?.subjectId === null
      )
    ) {
      errors.selectedSubjects = true as any
      setErrors(e => [
        ...e,
        `You need to select exactly ${subjectsToSelectNumber} subjects`
      ])
    }
    if (hasDuplicateIds(subjectsSelection.selectedSubjects)) {
      errors.selectedSubjects = true as any
      setErrors(e => [...e, 'You provide multiple same subjects'])
    }
    if (
      subjectsSelection.selectedSubjects.filter(s => s.isHighLevel).length !==
      app?.highLevelSubjectNumber
    ) {
      errors.selectedSubjects = true as any
      setErrors(e => [
        ...e,
        `You need to provide exaclty ${app?.highLevelSubjectNumber} high level and ${app?.standardLevelSubjectNumber} standard level subjects`
      ])
    }

    if (app?.selectionStatus === SelectionStatus.OPEN_FOR_CORRECTIONS) {
      subjectsSelection.selectedSubjects.map(sub => {
        const subject = subjects.find(s => s.id === sub.subjectId)!
        const existedInInitSubjects =
          initSubjectsSelection.selectedSubjects.some(
            s => s.id === sub.subjectId
          )
        const adder = existedInInitSubjects ? 0 : 1
        if (
          !sub.isHighLevel &&
          subject.minSLUsersNumber > adder + subject.selectionsNumber
        ) {
          errors.selectedSubjects = true as any
          setErrors(e =>
            Array.from(
              new Set([
                ...e,
                `${subject.name} does not reach minimum students number`
              ])
            )
          )
        }

        if (
          sub.isHighLevel &&
          subject.minHLUsersNumber > adder + subject.selectionsNumber
        ) {
          errors.selectedSubjects = true as any
          setErrors(e =>
            Array.from(
              new Set([
                ...e,
                `${subject.name} does not reach minimum students number`
              ])
            )
          )
        }
      })
    }

    const subIndexReq = subjectsSelection.selectedSubjects?.map(
      s => subjects.find(sub => s.subjectId === sub.id)!
    )

    const specificIndexRequirementsSubjects = subIndexReq?.map((s, i) => ({
      subjectId: s.id,
      name: s.name,
      indexRequirements: s.indexes.find(index => index.index === i + 1)
        ?.requirements
    }))

    for (const requirementsSubject of specificIndexRequirementsSubjects) {
      requirementsSubject.indexRequirements?.map(req => {
        if (req.requirement.type === IndexRequirementType.REQUIRE) {
          if (
            subjectsSelection.selectedSubjects[req.requirement.index! - 1]
              .subjectId !== req.requirement.subjectId
          ) {
            setErrors(e => [
              ...e,
              `Subject ${requirementsSubject.name} requires subject ${
                subjects.find(sub => sub.id === req.requirement.subjectId)?.name
              }`
            ])
          }
        } else if (req.requirement.type === IndexRequirementType.BLOCK) {
          if (
            subjectsSelection.selectedSubjects[req.requirement.index! - 1]
              .subjectId === req.requirement.subjectId
          ) {
            setErrors(e => [
              ...e,
              `Subject ${requirementsSubject.name} blocks subject ${
                subjects.find(sub => sub.id === req.requirement.subjectId)?.name
              }`
            ])
          }
        }
      })
    }

    return errors
  }

  const onSubmit = useCallback(
    async (subjectsSelection: {
      selectedSubjects: SelectedSubject[]
    }): Promise<void> => {
      setIsConfirmDialogOpen(true)
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: initSubjectsSelection,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  return (
    <div style={{ transform: 'scale(1)' }}>
      <form>
        <DialogTitle>Subject Selection</DialogTitle>
        <FormControl fullWidth style={{ flex: 1, marginTop: '1vh' }}>
          <InputLabel id='subjectGroups' required>
            STEP ONE
          </InputLabel>
          <Select
            labelId='subjectGroups'
            id='subjectGroups'
            value=''
            onChange={async (e: any) => {
              const subjectsToFill = subjectGroups.find(
                s => s.id === Number(e.target.value)
              )

              if (subjectsToFill?.subjectGroupProposal == null) {
                return
              }

              const _selectedSubjects = [...formik.values.selectedSubjects]
              for (const subjectToFill of subjectsToFill?.subjectGroupProposal) {
                _selectedSubjects[subjectToFill.index - 1] = {
                  ..._selectedSubjects[subjectToFill.index - 1]
                }

                const isHighLevel = !!subjects.find(
                  s1 => s1.id === subjectToFill.subjectId
                )?.isOnlyHL

                _selectedSubjects[subjectToFill.index - 1].subjectId =
                  subjectToFill.subjectId
                _selectedSubjects[subjectToFill.index - 1].isHighLevel =
                  isHighLevel
              }
              await formik.setFieldValue('selectedSubjects', _selectedSubjects)
            }}
          >
            {subjectGroups.map((group, index) => {
              const subjectIndexes = group.subjectGroupProposal.map(
                s => `${subjects.find(sub => sub.id === s.subjectId)?.name}`
              )
              return (
                <MenuItem key={index} value={group.id}>
                  {`${group.name}: ${subjectIndexes.join(', ')}`}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        <DialogContent>
          <div
            style={{ width: isMobile ? '90vw' : isLargeView ? '50vw' : '60vw' }}
          >
            {app?.selectionStatus === SelectionStatus.CLOSED && (
              <Alert severity='error' style={{ marginBottom: '2vh' }}>
                You cannot update suject selection in CLOSED selection status
              </Alert>
            )}
            {formik.values.selectedSubjects.map((s, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '1vh'
                }}
              >
                <FormControl fullWidth style={{ flex: 1 }}>
                  <InputLabel id={`subject${index + 1}`}>
                    Subject {index + 1}
                  </InputLabel>
                  <Select
                    disabled={index === 5 || index === 3 || index === 2}
                    labelId={`subject${index + 1}`}
                    id={`subject${index + 1}`}
                    value={s?.subjectId ?? ''}
                    label={`Subject ${index + 1}`}
                    onChange={e => {
                      setErrors([])

                      const _selectedSubjects = [
                        ...formik.values.selectedSubjects
                      ]

                      const isHighLevel = !!subjects.find(
                        s1 => s1.id === e.target.value
                      )?.isOnlyHL
                      _selectedSubjects[index] = {
                        subjectId: e.target.value,
                        isHighLevel
                      } as any
                      void formik.setFieldValue(
                        'selectedSubjects',
                        _selectedSubjects
                      )
                    }}
                  >
                    {subjects
                      .filter(s => s.indexes.some(i => i.index === index + 1))
                      .map((s, index) => (
                        <MenuItem key={index} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  style={{ marginLeft: '1vw' }}
                  control={
                    <Checkbox
                      color='primary'
                      disabled={
                        !!s.subjectId &&
                        (subjects.find(s1 => s1.id === s.subjectId)?.isOnlySL ||
                          subjects.find(s1 => s1.id === s.subjectId)?.isOnlyHL)
                      }
                      checked={
                        formik.values.selectedSubjects[index].isHighLevel
                      }
                      onClick={() => {
                        setErrors([])
                        const _selectedSubjects = [
                          ...formik.values.selectedSubjects
                        ]
                        _selectedSubjects[index] = {
                          ..._selectedSubjects[index]
                        }
                        _selectedSubjects[index].isHighLevel =
                          !_selectedSubjects[index].isHighLevel
                        void formik.setFieldValue(
                          'selectedSubjects',
                          _selectedSubjects
                        )
                      }}
                    />
                  }
                  label='HL'
                />
              </div>
            ))}
          </div>
        </DialogContent>
        {errors.map((e, i) => (
          <Alert key={i} severity='warning'>
            {e}
          </Alert>
        ))}
        <DialogActions>
          <Button
            onClick={async () => {
              await requestMaker.deleteSubjectSelection()
              dispatch(RefreshActions.refreshUserState())
              dispatch(
                RefreshActions.setToastMessage({
                  message: 'Succesfully reset subjects selection'
                })
              )
            }}
            disabled={app?.selectionStatus === SelectionStatus.CLOSED}
          >
            Reset
          </Button>
          <Button
            onClick={formik.handleSubmit as any}
            disabled={
              errors.length > 0 ||
              app?.selectionStatus === SelectionStatus.CLOSED ||
              formik.values.selectedSubjects.some(s => !s.subjectId)
            }
          >
            Add
          </Button>
        </DialogActions>
      </form>
      {isConfirmDialogOpen && (
        <ConfirmSelectionDialog
          selectedSubjects={formik.values.selectedSubjects}
          handleDialog={(b: boolean) => setIsConfirmDialogOpen(b)}
        />
      )}
    </div>
  )
}

function hasDuplicateIds (array: any[]) {
  const idSet: any = {}

  for (const item of array) {
    if (idSet[item.subjectId]) {
      return true
    }

    if (item?.subjectId) {
      idSet[item.subjectId] = true
    }
  }

  return false
}

export default SubjectsSelection
